import type { ComponentProps } from "react";
import { Switch as SwitchPrimitive } from "radix-ui";
import type { VariantProps } from "tailwind-variants";
import { tv } from "tailwind-variants";

const switchVariants = tv({
  slots: {
    root: "relative inline-flex h-fit shrink-0 cursor-pointer items-center overflow-hidden rounded-lg border border-neutral-300 bg-white text-neutral-950 shadow-xs transition-all focus-visible:ring-2 focus-visible:ring-secondary-200 focus-visible:ring-offset-2 focus-visible:ring-offset-secondary-200 focus-visible:outline-hidden disabled:cursor-not-allowed disabled:*:opacity-50 data-[state=checked]:[&>div>*:first-child]:text-neutral-700 data-[state=unchecked]:[&>div>*:last-child]:text-neutral-700",
    labels: "z-10 flex gap-6 px-3 py-2.5 font-medium",
    label: "transition-colors duration-500",
    thumb:
      "pointer-events-none absolute block h-full w-1/2 translate-x-full rounded-lg border-neutral-300 bg-primary-50 ring-0 transition-all duration-500 data-[state=checked]:right-11 data-[state=checked]:border-l data-[state=unchecked]:right-full data-[state=unchecked]:border-r",
  },
  variants: {
    variant: {
      default: {},
      simple: {
        root: "h-6 w-10 rounded-full bg-white focus-visible:ring-2 focus-visible:ring-secondary-200 focus-visible:ring-offset-2 focus-visible:ring-offset-secondary-200 focus-visible:outline-hidden disabled:cursor-not-allowed disabled:opacity-50 data-[state=checked]:bg-primary-800",
        thumb:
          "pointer-events-none block size-5 translate-x-5.5 rounded-full bg-white ring-0 transition-transform duration-100 data-[state=checked]:translate-x-10.5",
      },
    },
  },
  defaultVariants: {
    variant: "default",
  },
});

interface SwitchProps
  extends Omit<ComponentProps<typeof SwitchPrimitive.Root>, "asChild">,
    VariantProps<typeof switchVariants> {
  leftLabel?: string;
  rightLabel?: string;
}

export const Switch = ({
  className,
  leftLabel,
  rightLabel,
  variant = "default",
  ...props
}: SwitchProps) => {
  const { root, labels, label, thumb } = switchVariants({ variant });

  return (
    <SwitchPrimitive.Root className={root({ className })} {...props}>
      {variant === "default" && (
        <div className={labels()}>
          <span className={label()}>{leftLabel}</span>
          <span className={label()}>{rightLabel}</span>
        </div>
      )}

      <SwitchPrimitive.Thumb className={thumb()} />
    </SwitchPrimitive.Root>
  );
};
